import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'

import { assetUrl } from 'utils/url'
import { Header } from 'app/KinerjaShared/components/Header'
import { ConfirmationDialog } from 'app/KinerjaShared/components/ConfirmationDialog'
import {
  API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIPelaksanaanDocumentPreparation,
  useAPIPelaksanaanProgress,
  useAPIUpsertPelaksanaanDocumentPreparation,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { TargetPerilakuForm } from 'app/KinerjaPamongPage/pages/pegawai/pelaksanaan/DiskusiPersiapan/TargetPerilakuForm'
import { JadwalObservasiForm } from 'app/KinerjaPamongPage/pages/pegawai/pelaksanaan/DiskusiPersiapan/JadwalObservasiForm'
import { PageGuardPamongPelaksanaanStatus } from 'app/KinerjaPamongPage/components/pelaksanaan/PageGuardPamongPelaksanaanStatus'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'
import { LangkahObservasiPraktikKinerjaSection } from 'app/KinerjaShared/components/LangkahObservasiPraktikKinerjaSection'
import { useBackNavigationConfirmation } from 'app/KinerjaShared/hooks/useBackNavigationConfirmation'

import type {
  IPelaksanaanDocumentPreparationForm,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'
import type { CustomError } from 'utils/customError'

const STEP: TStatePelaksanaan = 'DOCUMENT_PREPARATION'
const STEP_TITLE = PRAKTIK_KINERJA_STEPS[STEP].title

export function DiskusiPersiapan() {
  // -------------------------
  // Hooks & Context
  // -------------------------
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { periodNo, periodYear } = usePeriodContext()

  const praktikKinerjaHubRoute = `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`

  // -------------------------
  // Handle dialogs
  // -------------------------
  const [activeDialog, setActiveDialog] = useState<
    'save-draft' | 'submit' | 'unsaved-edit' | null
  >(null)

  const handleCloseDialog = useCallback(() => {
    setActiveDialog(null)
  }, [])

  // -------------------------
  // Form State
  // -------------------------
  const formMethods = useForm<IPelaksanaanDocumentPreparationForm>({
    defaultValues: {
      behavioralTargets: [],
      behavioralTargetEffort: '',
      observationDate: '',
      observationLocation: {
        npsn: '',
      },
      observationTool: '',
      observationNote: '',
      observationDocuments: [],
    },
  })

  const { formState, getValues, reset } = formMethods

  // -------------------------
  // Get pelaksanaan progress data
  // -------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isLoadingPelaksanaanProgress,
  } = useAPIPelaksanaanProgress({
    tipe: 'SPNF',
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false, // Prevent double fetch on re-render after invalidation
      refetchOnWindowFocus: false, // Prevent double fetch on window focus
    },
  })
  const isEvaluated =
    pelaksanaanProgressData?.data?.evaluationDocumentState === 'READY'

  // -------------------------
  // Get Persiapan data
  // -------------------------
  const { data: documentData, isLoading: isLoadingDocumentPreparation } =
    useAPIPelaksanaanDocumentPreparation({
      tipe: 'SPNF',
      opts: {
        useErrorBoundary: false,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })
  const isSubmitted = documentData?.data?.state === 'SUBMITTED'

  // Sync Persiapan data with form
  useEffect(() => {
    const data = documentData?.data

    if (!data) return

    reset({
      observationTool: data.detail.observationTool || '',
      observationNote: data.detail.observationNote || '',
      observationDate: data.detail.observationDate || '',
      observationMeeting: data.detail.observationMeeting || '',
      behavioralTargets: data.detail.behavioralTargets.map((item) => item.id),
      behavioralTargetEffort: data.detail.behavioralTargetEffort || '',
    })
  }, [documentData?.data, reset])

  // -------------------------
  // Other derived values
  // -------------------------
  const isLoadingData =
    isLoadingPelaksanaanProgress || isLoadingDocumentPreparation

  // -------------------------
  // Handle form actions
  // -------------------------
  const upsertMutation = useAPIUpsertPelaksanaanDocumentPreparation()
  const isSubmitting = upsertMutation.isLoading
  const isSaveDraftDisabled = !formState.isDirty || isSubmitting
  const shouldDisableInputs = isSubmitted || isEvaluated || isSubmitting

  const handleSubmit = useCallback(() => {
    upsertMutation.mutate(
      {
        state: 'SUBMITTED',
        detail: getValues(),
        tipe: 'SPNF',
      },
      {
        onSuccess: () => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })

          queryClient.invalidateQueries(
            API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION
          )

          toast({
            type: 'success',
            message: `Berhasil kirim ${STEP_TITLE}!`,
          })

          router.replace(
            `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
          )
        },
        onError: (err: CustomError) => {
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(
              `Gagal kirim ${STEP_TITLE}. Coba lagi`,
              errorCode
            ),
          })
        },
        onSettled: () => {
          handleCloseDialog()
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSaveDraft = useCallback(
    () => {
      upsertMutation.mutate(
        {
          state: 'DRAFT',
          detail: getValues(),
          tipe: 'SPNF',
        },
        {
          onSuccess: () => {
            // Invalidate without auto-refetch to prevent double fetch
            // React Query will refetch when needed
            // and deduplicate if multiple components share the same query key
            queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
              refetchActive: false,
              refetchInactive: false,
            })

            queryClient.invalidateQueries(
              API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION
            )

            toast({
              type: 'success',
              message: 'Berhasil simpan draf!',
            })

            router.replace(
              `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
            )
          },
          onError: (err: CustomError) => {
            const errorCode = getErrorCode(err)

            toast({
              message: generateErrorMessage(
                'Gagal simpan draf. Coba lagi',
                errorCode
              ),
            })
          },
          onSettled: () => {
            handleCloseDialog()
          },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues]
  )

  // -----------------------------------------
  // Enable unsaved changes back navigation guard
  // -----------------------------------------
  useBackNavigationConfirmation({
    shouldConfirm: !isSaveDraftDisabled,
    onInterceptBack: () => setActiveDialog('unsaved-edit'),
  })

  // -----------------------------------------
  // Handle back navigation with
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: praktikKinerjaHubRoute,
  })

  // -----------------------------------------
  // Handle redirect to Praktik Kinerja Hub page
  // -----------------------------------------
  // Uses router.replace() directly to bypass beforePopState guard

  const handleGoToPraktikKinerjaHub = useCallback(() => {
    router.replace(praktikKinerjaHubRoute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [praktikKinerjaHubRoute])

  return (
    <div className="pt-14 px-8 pb-8">
      <div className="mx-auto max-w-6xl">
        <Header title={STEP_TITLE} onBack={handleBack} />

        <PageGuardPamongPelaksanaanStatus
          periodParams={{ skpPeriod: periodNo, skpYear: periodYear }}
          targetStep={STEP}
          unstartedRedirectRoute={PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}
          incorrectOrderRedirectRoute={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
        >
          {isLoadingData ? (
            <div
              className="flex flex-col gap-4 justify-center items-center mt-9"
              aria-busy="true"
            >
              <Spinner data-testid="loading-spinner" />
            </div>
          ) : (
            <div className="max-w-5xl m-auto">
              <LangkahObservasiPraktikKinerjaSection
                className="mt-9"
                steps={[
                  {
                    title: 'Diskusi dengan Kepala SKB',
                    description: `Agar lebih mudah isi ${STEP_TITLE}.`,
                  },
                  {
                    title: 'Bersiap untuk observasi',
                    description: `Lengkapi isi ${STEP_TITLE}.`,
                  },
                  {
                    title: 'Pelaksanaan Observasi',
                    description: 'Semangat dan semoga lancar!',
                  },
                ]}
              />
              <div className="flex flex-row justify-between items-center mt-9 bg-blue-5 p-6 border-t border-x rounded-t border-subdued">
                <Text variant="heading-lg" as="h2">
                  {STEP_TITLE}
                </Text>
              </div>
              <FormProvider {...formMethods}>
                <form
                  onSubmit={formMethods.handleSubmit(() =>
                    setActiveDialog('submit')
                  )}
                  className="flex flex-col bg-white border-x border-b rounded-b border-subdued"
                >
                  <TargetPerilakuForm
                    disabled={shouldDisableInputs}
                    pelaksanaanProgressData={pelaksanaanProgressData.data}
                    isLoadingPelaksanaanProgress={isLoadingPelaksanaanProgress}
                  />
                  <div className="p-6 bg-blue-5">
                    <Text variant="heading-sm" as="h3">
                      Jadwal Observasi Praktik Kinerja
                    </Text>
                  </div>
                  <JadwalObservasiForm disabled={shouldDisableInputs} />
                  <div className="p-6 border-t border-subdued">
                    <div className="bg-gray-10 rounded-lg flex flex-row items-center pl-6 py-6 pr-44 gap-3 relative min-h-24">
                      <Icon className="text-gray-90">info</Icon>
                      <Text variant="heading-sm" className="flex-1">
                        Persiapkan dan diskusikan dengan Kepala SKB terkait
                        perangkat pendukung Praktik Kinerja.
                      </Text>
                      <img
                        src={assetUrl(
                          '/images/pengelolaan-kinerja/img-discussion.png'
                        )}
                        alt=""
                        className="absolute right-0 bottom-0 object-contain max-h-24 pr-4"
                        height={100}
                        width={200}
                      />
                    </div>
                  </div>
                  {!isEvaluated && !isSubmitted && (
                    <div className="p-6 flex flex-row space-x-2.5 justify-end border-t border-subdued">
                      <Button
                        color="white"
                        size="lg"
                        onClick={() => setActiveDialog('save-draft')}
                        disabled={isSaveDraftDisabled}
                        isLoading={isSubmitting}
                      >
                        Simpan draf
                      </Button>
                      <Button size="lg" type="submit" isLoading={isSubmitting}>
                        Kirim
                      </Button>
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
          )}
          <ConfirmationDialog
            title="Simpan draf sebelum keluar?"
            description="Pilihan Anda akan disimpan dan bisa dilanjutkan nanti. Jika lewat batas waktu, draf akan otomatis dikumpulkan."
            confirmCta="Simpan dan keluar"
            cancelCta="Kembali"
            isOpen={activeDialog === 'save-draft'}
            isLoading={upsertMutation.isLoading}
            onCancel={handleCloseDialog}
            onClose={handleCloseDialog}
            onConfirm={handleSaveDraft}
          />
          <ConfirmationDialog
            title="Perubahan belum disimpan"
            description="Mohon simpan perubahan terlebih dahulu agar dapat dilanjutkan nanti."
            confirmCta="Simpan"
            cancelCta="Lanjut keluar"
            isOpen={activeDialog === 'unsaved-edit'}
            isLoading={upsertMutation.isLoading}
            onCancel={handleGoToPraktikKinerjaHub}
            onClose={handleCloseDialog}
            onConfirm={handleSaveDraft}
          />
          <ConfirmationDialog
            title={`Kumpulkan ${STEP_TITLE}?`}
            description="Anda dapat ubah isi dokumen jika diperlukan, selama belum dinilai oleh Kepala SKB."
            confirmCta="Konfirmasi"
            cancelCta="Kembali"
            topContent={
              <img
                src={assetUrl('/illustrations/img-publish-data.png')}
                alt=""
                height={84}
                width={84}
              />
            }
            isOpen={activeDialog === 'submit'}
            isLoading={upsertMutation.isLoading}
            onCancel={handleCloseDialog}
            onClose={handleCloseDialog}
            onConfirm={handleSubmit}
          />
        </PageGuardPamongPelaksanaanStatus>
      </div>
    </div>
  )
}
